import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField, LoadingContent } from 'components'
import { editDeclarationOfOwnershipSignerInfo, getDeclarationOfOwnershipSignerInfo } from 'controllers'
import { downloadFileName } from 'controllers/documents/index.'
import { PersonDto } from 'dtos'
import { DeclarationOfOwnershipSignerInfoDto, DeclarationOfOwnershipSignerInit } from 'dtos/people'
import { useEffect, useState } from 'react'
import DeclarationOfOwnershipSignerEditDialog from './DeclarationOfOwnershipSignerEditDialog'
import { enqueueSnackbar } from 'notistack'

interface Props {
  person: PersonDto
}

export default function DeclarationOfOwnershipSignerCard({ person }: Props) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<DeclarationOfOwnershipSignerInfoDto>(DeclarationOfOwnershipSignerInit)
  const [isGetting, setIsGetting] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    if (!person.personGuid) return

    setIsGetting(true)
    getDeclarationOfOwnershipSignerInfo(person.personGuid)
      .then(res => setInitialValues(res.value))
      .finally(() => setIsGetting(false))
  }, [person])

  return (
    <>
      <DeclarationOfOwnershipSignerEditDialog
        initialValues={initialValues}
        loading={isSaving}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSaving(true)
          editDeclarationOfOwnershipSignerInfo(person.personGuid!, values)
            .then(res => {
              enqueueSnackbar('Declaration of Ownership Signer Saved Successfully!', { variant: 'success' })
              setInitialValues(res.value)
              setDialogOpen(false)
            })
            .finally(() => setIsSaving(false))
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Declaration of Ownership Signer
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button endIcon={<Edit />} onClick={() => setDialogOpen(true)} variant='text'>
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <LoadingContent isLoading={isGetting}>
              <Grid item xs={12}>
                <LabelField
                  label='Signature'
                  onClick={
                    initialValues.declarationOfOwnershipSignerSignatureFileName
                      ? () => downloadFileName(initialValues.declarationOfOwnershipSignerSignatureFileName!)
                      : undefined
                  }
                  value={initialValues.declarationOfOwnershipSignerSignatureFileName ?? 'No file uploaded'}
                  error={!initialValues.declarationOfOwnershipSignerSignatureFileName}
                />
              </Grid>
            </LoadingContent>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`This card only shows if person is marked as Declaration of Ownership Signer.

Only one Declaration of Ownership Signer can exist.

The Signature uploaded here will appear on the Declaration of Ownership form included in the Send to Broker documents generated for inbound trips.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
